import React, { useState, useEffect } from 'react'
import { Typography, Button, notification } from 'antd'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import axios, { AxiosError, HttpStatusCode } from 'axios'

import { FlexRow } from '../../FlexboxReusables/FlexItems'
import LoadingSpinner from '../../LoadingSpinner'
import Medal from '../../../assets/Medal'
import type { ExamResults as ExamResultsType } from '../Context/types'
import { apiUrl, certificatesUrl } from '../../../globals'
import NotFound from '../../../Routes/PageNotFound'
import AuthenticationModal from './AuthenticationModal'

const ExamResults = () => {
  const examAttemptId = useParams<{ examAttemptId: string }>().examAttemptId ?? ''
  const [ loading, setLoading ] = useState<boolean>(true)
  const [ examResults, setExamResults ] = useState<ExamResultsType | undefined>(undefined)
  const [ showAuthenticateModal, setShowAuthenticateModal ] = useState<boolean>(false)
  const [ firstName, setFirstName ] = useState('')
  const [ lastName, setLastName ] = useState('')
  const [ birthDate, setBirthDate ] = useState('')

  const examAttemptIdInt = parseInt(examAttemptId)

  useEffect(() => {
    fetchResults()
  }, [])

  const fetchResults = async () => {
    const response = await axios.get(`${apiUrl}/get-exam-result/${examAttemptId}`)
    const { data } = response

    setExamResults(data)
    setLoading(false)
  }
  
  if (loading) {
    return <LoadingSpinner loadingText='Lade Ergebnisse' /> // Todo: i18n
  }

  if (!examResults) {
    return <NotFound />
  }

  const { hasPassed } = examResults
  
  const getCertificate = async () => {
    const userData = localStorage.getItem('examUserData')

    if (!userData) {
      setShowAuthenticateModal(true)

      return
    }

    const { firstName, lastName, birthDate } = JSON.parse(userData)
    setFirstName(firstName)
    setLastName(lastName)
    setBirthDate(birthDate)

    try {
      const pdfResponse = await axios.post<GetCertificateResponse>(`${apiUrl}/get-certificate`, {
        examAttemptId,
        firstName,
        lastName,
        birthDate
      })

      const { examAttemptYear, examAttemptMonth } = pdfResponse.data
      const downloadLink = `${certificatesUrl}/${examAttemptYear}/${examAttemptMonth}/${examAttemptId}/Certificate.pdf`

      window.location.href = downloadLink
    } catch (err) {
      if (err instanceof AxiosError && err.response?.status === HttpStatusCode.BadRequest) {
        notification.error({
          message: 'Ungültige Daten',
          description: 'Die eingegebenen Daten stimmen nicht mit den hinterlegten Daten überein.',
          placement: 'bottomRight',
        })

        // assume that the data is invalid and give the user the possibility to re-enter it
        localStorage.removeItem('examUserData')
        setShowAuthenticateModal(true)
      }
    }
  }

  return (
    <>
      <FlexRow justifyContent='center' margin='1em 0' padding='1em'>
        <Typography.Title level={1}>
          {
            hasPassed
              ? <PassedTitle />
              : <FailedTitle />
          }
        </Typography.Title>
      </FlexRow>
      <FlexRow justifyContent='center' margin='3em 0'>
        {hasPassed ? (
          <Button
            type='primary'
            onClick={() => getCertificate()}
          >
          Zertifikat herunterladen
          </Button>
        ) : (
          <Button
            type='primary'
            href={`/exams/${examResults.originalLinkForParticipants}`}
          >
            Zurück zur Schulung
          </Button>
        )}
      </FlexRow>
      {showAuthenticateModal && (
        <AuthenticationModal
          examAttemptId={examAttemptIdInt}
          showAuthenticateModal={showAuthenticateModal}
          setShowAuthenticateModal={setShowAuthenticateModal}
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          birthDate={birthDate}
          setBirthDate={setBirthDate}
        />
      )}
    </>
  )
}

const PassedTitle = () => (
  <FlexRow justifyContent='center' flexWrap>
    <Medal width='2em' height='2em'/>
    <FormattedMessage id='exam.examResults.passed' />
  </FlexRow>
)

const FailedTitle = () => (
  <FormattedMessage id='exam.examResults.failed' />
)

export default ExamResults

export type GetCertificateResponse = {
  examAttemptYear: number
  examAttemptMonth: number
  examAttemptId: string
}
